import 'regenerator-runtime/runtime';
import { call, put, fork, select, takeEvery } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { mangoUserId } from 'selectors/user';
import { parseError } from 'logic/helpers';
import { clearStoredScaActionUrl, clearResumeFailed } from 'logic/sca';
import * as actions from '../actions';
import * as constants from '../constants';
import * as loginService from '../services/login';
import api from '../api';

function cleanUpUserData(user) {
  return {
    ...user,
    isCompany: user.type_of_person === 'LEGAL',
  };
}

function* deleteUserRequest() {
  try {
    yield call(api.deleteUser);
    yield put(actions.postLogoutSuccess({ success: { logout: 'Success' } }));

    setTimeout(() => {
      window.location.replace('/');
    }, 3000);
  } catch (err) {
    yield put(actions.deleteUserFailure(parseError(err)));
  }
}

function* fetchUserSelfRequest() {
  try {
    let user = yield call(api.fetchUserSelf.bind(null));
    user = cleanUpUserData(user);
    yield put(actions.fetchUserSelfSuccess(user));
  } catch (e) {
    yield [put(actions.fetchUserSelfFailure({ errors: {} }))];
  }
}

function* fetchUserSelfFailure() {
  yield call(delay, 400);
}

function* postLoginRequest(req) {
  try {
    const { username, password } = req.payload;
    const login = yield call(api.postLogin.bind(null, { username, password }));
    const parseLoginResponse = loginService.checkLoginResponse(login);

    if (!parseLoginResponse) {
      throw new Error('Failed login');
    }

    // Clear any stale SCA / resume info on successful login
    yield call(clearStoredScaActionUrl);
    yield call(clearResumeFailed);

    yield [
      put(actions.postLoginSuccess({ isLoggedIn: parseLoginResponse })),
      put(actions.fetchUserSelfRequest()),
      put(actions.fetchSelfEvents()),
    ];
  } catch (e) {
    yield put(actions.postLoginFailure(parseError(e)));
  }
}

function* postLogoutRequest() {
  try {
    yield call(api.postLogout.bind(null));
    yield put(actions.postLogoutSuccess({ success: { logout: 'Successfully logged out' } }));

    // Clear any stale SCA / resume info on successful login
    yield call(clearStoredScaActionUrl);
    yield call(clearResumeFailed);

    window.location.replace('/');
  } catch (e) {
    yield put(actions.postLogoutFailure({ errors: { logout: 'Failed logout' } }));
  }
}

function* postCreateUserRequest(req) {
  try {
    const { username, name } = req.payload;
    const data = yield call(api.postCreateUser.bind(null, username, name));
    yield put(actions.postCreateUserSuccess(data));
  } catch (e) {
    yield put(actions.postLoginFailure(parseError(e)));
  }
}

function* updateUserRequest(req) {
  const { triggerSCA, ...payload } = req.payload;

  try {
    let user = yield call(api.updateUser.bind(null, payload));
    user = cleanUpUserData(user);
    yield put(actions.updateUserSuccess(user));
    // Exemption from Mangopay, user remains ACTIVE
    /* if (triggerSCA) {
      yield put(actions.scaTriggerOn());
    } */
  } catch (e) {
    yield put(actions.updateUserFailure(parseError(e)));
  }
}

function* fetchUploadedKYCRequest(req) {
  try {
    const data = yield call(api.fetchUploadedKYC.bind(null, req.payload));
    yield put(actions.fetchUploadedKYCSuccess(data));
  } catch (e) {
    yield put(actions.fetchUploadedKYCFailure(parseError(e)));
  }
}

function* uploadIDProofRequest(req) {
  try {
    const mangoUserID = yield select(mangoUserId);

    const data = yield call(api.uploadIDProof.bind(null, req.payload));
    yield put(actions.uploadIDProofSuccess(data));

    // if the user didnt had mango, fetch again to get the newly created on the backend
    if (mangoUserID === null) {
      yield call(fetchUserSelfRequest.bind(null));
    }
    let user = yield call(api.fetchUserSelf.bind(null));
    user = cleanUpUserData(user);
    yield put(actions.fetchUserSelfSuccess(user));
  } catch (e) {
    yield put(actions.uploadIDProofFailure(parseError(e)));
  }
}

function* fetchSelfEvents() {
  try {
    const events = yield call(api.fetchSelfEvents.bind(null));
    yield put(actions.fetchSelfEventsSuccess(events));
  } catch (e) {
    yield [put(actions.fetchUserSelfFailure({ errors: {} }))];
  }
}

function* updateSelfEvents(req) {
  try {
    const events = yield call(api.updateSelfEvents.bind(null, req.payload));
    yield put(actions.fetchSelfEventsSuccess(events));
  } catch (e) {
    yield put(actions.fetchUserSelfFailure({ errors: {} }));
  }
}

function* fetchTodos() {
  try {
    const todos = yield call(api.fetchTodos.bind(null));
    yield put(actions.fetchTodosSuccess(todos));
  } catch (e) {
    yield put(actions.fetchUserSelfFailure({ errors: {} }));
  }
}

export function* fetchSessionStatus() {
  try {
    const data = yield call(api.fetchSessionStatus);
    yield put(actions.fetchSessionStatusSuccess(data));
  } catch (e) {
    yield put(actions.fetchSessionStatusFailure());
  }
}

export default [
  fork(takeEvery, constants.FETCH_USER_SELF_REQUEST, fetchUserSelfRequest),
  fork(takeEvery, constants.FETCH_USER_SELF_FAILURE, fetchUserSelfFailure),
  fork(takeEvery, constants.POST_LOGOUT_REQUEST, postLogoutRequest),
  fork(takeEvery, constants.POST_LOGIN_REQUEST, postLoginRequest),
  fork(takeEvery, constants.LOGIN_CREATE_USER_REQUEST, postCreateUserRequest),
  fork(takeEvery, constants.UPDATE_USER_REQUEST, updateUserRequest),
  fork(takeEvery, constants.FETCH_UPLOADED_KYC_REQUEST, fetchUploadedKYCRequest),
  fork(takeEvery, constants.UPLOAD_IDPROOF_REQUEST, uploadIDProofRequest),

  fork(takeEvery, constants.DELETE_USER_REQUEST, deleteUserRequest),

  fork(takeEvery, constants.FETCH_SELF_EVENTS_REQUEST, fetchSelfEvents),
  fork(takeEvery, constants.UPDATE_SELF_EVENTS_REQUEST, updateSelfEvents),

  fork(takeEvery, constants.FETCH_TODOS_REQUEST, fetchTodos),
  fork(takeEvery, constants.FETCH_SESSION_STATUS_REQUEST, fetchSessionStatus),
];
